import { useState } from "react";
import { Download, RefreshCw, Trash2 } from "lucide-react";
import { api, formatDate } from "../../api";
import { useApi } from "../../hooks";
import { Sparkline } from "../Charts";
import { EmptyState, ErrorState, LoadingState, StatusBadge } from "../Ui";
import { useShowMore } from "../Workflow";
import {
  TAG_LABELS, bidRange, competitionTone, filterRows, fmtPosition, fmtVolume, sortRows, trendValues,
  type SavedRow,
} from "../../keywords";
import { SortHeader, type Sort } from "./shared";

const API_BASE = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");

export function SavedListTab({ clientId, tags, onFind }: { clientId: number; tags: string[]; onFind?: () => void }) {
  const list = useApi<{ rows: SavedRow[] }>(`/api/keywords/${clientId}/list`);
  const [text, setText] = useState("");
  const [tag, setTag] = useState("all");
  const [where, setWhere] = useState("all");
  const [sort, setSort] = useState<Sort>({ key: "avg_monthly_searches", dir: "desc" });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ text: string; warn: boolean } | null>(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<{ id: number; notes: string } | null>(null);

  const rows = list.data?.rows || [];
  const places = [...new Set(rows.map((r) => r.geo_label))];
  const shown = sortRows(filterRows(rows, { text, minVolume: 0 }).filter((r) => (tag === "all" || r.tag === tag) && (where === "all" || r.geo_label === where)), sort.key, sort.dir);
  const { visible, control } = useShowMore(shown, 100, `${text}|${tag}|${where}|${sort.key}|${sort.dir}`);

  const replace = (id: number, patch: Partial<SavedRow>) => list.setData((d) => (d ? { rows: d.rows.map((r) => (r.id === id ? { ...r, ...patch } : r)) } : d));
  async function patch(row: SavedRow, body: { tag?: string; notes?: string | null }) {
    setError("");
    try {
      await api(`/api/keywords/${clientId}/list/${row.id}`, { method: "PATCH", body: JSON.stringify(body) });
      replace(row.id, body);
      return true;
    } catch (e) { setError(e instanceof Error ? e.message : "Could not save"); return false; }
  }
  async function remove(row: SavedRow) {
    if (!window.confirm(`Remove "${row.keyword}" (${row.geo_label}) from the list?`)) return;
    setError("");
    try {
      await api(`/api/keywords/${clientId}/list/${row.id}`, { method: "DELETE" });
      list.setData((d) => (d ? { rows: d.rows.filter((r) => r.id !== row.id) } : d));
    } catch (e) { setError(e instanceof Error ? e.message : "Could not remove"); }
  }
  async function refresh() {
    setBusy(true); setError(""); setNotice(null);
    try {
      const r = await api<{ refreshed: number; failed: Array<{ error: string }> }>(`/api/keywords/${clientId}/list/refresh`, { method: "POST", body: "{}" });
      setNotice(r.failed.length
        ? { text: `Updated ${r.refreshed}. ${r.failed[0].error} The rest are retried tonight.`, warn: true }
        : { text: `Updated the numbers for ${r.refreshed} keyword${r.refreshed === 1 ? "" : "s"}.`, warn: false });
      list.reload();
    } catch (e) { setError(e instanceof Error ? e.message : "Could not refresh"); }
    finally { setBusy(false); }
  }

  if (list.loading) return <LoadingState label="Loading the keyword list" />;
  if (list.error) return <ErrorState message={list.error} onRetry={list.reload} />;
  if (!rows.length) return (
    <section className="panel">
      <EmptyState
        title="No saved keywords yet"
        description="Find ideas on the Research tab or pick searches on the Search Console tab, tick the ones that matter and save them here with a tag."
        action={onFind ? <button type="button" className="button" onClick={onFind}>Find ideas</button> : undefined}
      />
    </section>
  );

  return (
    <section className="panel">
      <div className="panel-heading">
        <div><h2>Saved keywords</h2><p>{shown.length.toLocaleString()} of {rows.length.toLocaleString()} · numbers refresh monthly</p></div>
        <div className="kw-controls">
          <input type="search" placeholder="Filter keywords" aria-label="Filter keywords" value={text} onChange={(e) => setText(e.target.value)} />
          <select aria-label="Tag" value={tag} onChange={(e) => setTag(e.target.value)}>
            <option value="all">All tags</option>
            {tags.map((t) => <option key={t} value={t}>{TAG_LABELS[t] || t}</option>)}
          </select>
          {places.length > 1 && (
            <select aria-label="Location" value={where} onChange={(e) => setWhere(e.target.value)}>
              <option value="all">All locations</option>
              {places.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          )}
          <button type="button" className="button secondary" disabled={busy} onClick={() => void refresh()}><RefreshCw className={busy ? "spin" : ""} aria-hidden="true" />{busy ? "Refreshing…" : "Refresh numbers"}</button>
          <a className="button secondary" href={`${API_BASE}/api/keywords/${clientId}/list.csv`} download><Download aria-hidden="true" />Export CSV</a>
        </div>
      </div>
      {notice && <p role="status" className={`inline-notice ${notice.warn ? "warning" : "success"}`}>{notice.text}</p>}
      {error && <p role="alert" className="inline-notice danger">{error}</p>}
      <div className="table-wrap">
        <table className="kw-table kw-saved">
          <thead><tr>
            <SortHeader label="Keyword" k="keyword" sort={sort} setSort={setSort} />
            <SortHeader label="Tag" k="tag" sort={sort} setSort={setSort} />
            <SortHeader label="Searches / mo" k="avg_monthly_searches" sort={sort} setSort={setSort} numeric />
            <th>12 months</th>
            <SortHeader label="Competition" k="competition_index" sort={sort} setSort={setSort} numeric />
            <SortHeader label="Top of page bid" k="high_bid" sort={sort} setSort={setSort} numeric />
            <SortHeader label="Search Console" k="sc_clicks" sort={sort} setSort={setSort} numeric title="Clicks and average position, last 90 days" />
            <SortHeader label="BrightLocal" k="bl_maps" sort={sort} setSort={setSort} numeric title="Best Google and Maps rank from the Rank Tracker" />
            <SortHeader label="Numbers as of" k="metrics_at" sort={sort} setSort={setSort} numeric />
            <th><span className="sr-only">Actions</span></th>
          </tr></thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.id}>
                <td data-label="Keyword">
                  <strong>{r.keyword}</strong>
                  <small className="kw-sub">{r.geo_label}</small>
                  {editing?.id === r.id ? (
                    <span className="kw-notes-edit">
                      <input aria-label={`Notes for ${r.keyword}`} value={editing.notes} maxLength={1000} autoFocus
                        onChange={(e) => setEditing({ id: r.id, notes: e.target.value })}
                        onKeyDown={(e) => { if (e.key === "Escape") setEditing(null); if (e.key === "Enter") void patch(r, { notes: editing.notes.trim() || null }).then((ok) => ok && setEditing(null)); }} />
                      <button type="button" className="text-button" onClick={() => void patch(r, { notes: editing.notes.trim() || null }).then((ok) => ok && setEditing(null))}>Save</button>
                      <button type="button" className="text-button" onClick={() => setEditing(null)}>Cancel</button>
                    </span>
                  ) : (
                    <button type="button" className="text-button kw-notes" onClick={() => setEditing({ id: r.id, notes: r.notes || "" })}>{r.notes || "Add note"}</button>
                  )}
                </td>
                <td data-label="Tag">
                  <select className="kw-tag" aria-label={`Tag for ${r.keyword}`} value={r.tag} onChange={(e) => void patch(r, { tag: e.target.value })}>
                    {tags.map((t) => <option key={t} value={t}>{TAG_LABELS[t] || t}</option>)}
                  </select>
                </td>
                <td className="number-cell" data-label="Searches / mo">{fmtVolume(r.avg_monthly_searches)}</td>
                <td data-label="12 months"><Sparkline values={trendValues(r.monthly)} label={`Monthly searches for ${r.keyword}: ${trendValues(r.monthly).join(", ")}`} /></td>
                <td data-label="Competition">{r.competition ? <StatusBadge tone={competitionTone(r.competition)}>{r.competition}</StatusBadge> : <span className="muted">—</span>}</td>
                <td className="number-cell" data-label="Top of page bid">{bidRange(r.low_bid, r.high_bid, r.currency)}</td>
                <td className="number-cell" data-label="Search Console">{r.sc_clicks != null ? <>{fmtVolume(r.sc_clicks)} clicks<small>pos. {fmtPosition(r.sc_position)}</small></> : <span className="muted">—</span>}</td>
                <td className="number-cell" data-label="BrightLocal">
                  {r.bl_google != null || r.bl_maps != null || r.bl_volume != null ? (
                    <>{r.bl_google != null ? `#${r.bl_google}` : "—"} / {r.bl_maps != null ? `#${r.bl_maps} Maps` : "— Maps"}{r.bl_volume != null && <small>{fmtVolume(r.bl_volume)} / mo</small>}</>
                  ) : <span className="muted">—</span>}
                </td>
                <td data-label="Numbers as of">
                  {r.metrics_error
                    ? <span title={r.metrics_error}><StatusBadge tone="warning">{r.metrics_at ? "Refresh failed" : "Not fetched yet"}</StatusBadge></span>
                    : r.metrics_at ? formatDate(r.metrics_at) : <span className="muted">Pending</span>}
                </td>
                <td><button type="button" className="icon-button" aria-label={`Remove ${r.keyword}`} title="Remove" onClick={() => void remove(r)}><Trash2 aria-hidden="true" size={15} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!shown.length && <p className="muted kw-empty">No saved keywords match these filters.</p>}
      {control}
    </section>
  );
}
